import React, { useState } from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { inquiryApi } from '../api/inquiryApi';

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  interestedAs: 'client',
  topic: 'General question',
  location: '',
  message: '',
};

const isEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.name.trim()) return setError('Please enter your name.');
    if (!isEmail(form.email)) return setError('Please enter a valid email address.');
    if (!form.message.trim()) return setError('Please tell us how we can help.');

    setStatus('submitting');

    try {
      await inquiryApi.create({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        interestedAs: form.interestedAs,
        serviceInterest: `${form.topic}: ${form.message.trim()}`,
        location: form.location.trim(),
      });
      setStatus('success');
      setForm(EMPTY_FORM);
    } catch (err) {
      setStatus('idle');
      setError(err?.message || 'We could not send your message. Please try again.');
    }
  };

  const inputClass =
    'box-border w-full   !rounded-2xl !border-0 bg-slate-50 px-4 py-3.5 text-[15px] font-medium text-slate-900 !outline-none !ring-1 !ring-inset !ring-slate-200 !shadow-[0_4px_12px_rgba(15,23,42,0.035)] transition-all duration-200 ease-out placeholder:font-normal placeholder:text-slate-400 hover:bg-white hover:!ring-slate-300 hover:!shadow-[0_7px_18px_rgba(15,23,42,0.06)] focus:bg-white focus:!ring-2 focus:!ring-green-500 focus:!shadow-[0_0_0_4px_rgba(34,197,94,0.10),0_8px_22px_rgba(15,23,42,0.07)]';

  const buttonClass =
    'transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 active:scale-[0.99]';

  if (status === 'success') {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center rounded-3xl border border-green-100 bg-white p-6 text-center shadow-[0_18px_45px_rgba(15,23,42,0.09)] sm:p-8 xl:justify-self-end">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-700 shadow-inner">
          <CheckCircle2 size={30} />
        </span>
        <h3 className="mb-0 mt-5 text-2xl font-bold text-slate-950">Message sent</h3>
        <p className="mb-0 mt-3 max-w-sm text-sm leading-6 text-slate-600">
          Thanks for contacting Giggs. Our team has received your message and will respond using the details you provided.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className={`mt-6 cursor-pointer rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-800 hover:border-green-200 hover:bg-green-50 ${buttonClass}`}
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto w-full max-w-3xl rounded-3xl border border-slate-200/90 bg-white p-5 shadow-[0_18px_45px_rgba(15,23,42,0.09)] transition-shadow duration-300 hover:shadow-[0_22px_52px_rgba(15,23,42,0.11)] sm:p-7 lg:p-8 xl:justify-self-end"
      noValidate
    >
      <div className="mb-6 sm:mb-7">
        <span className="text-xs font-bold uppercase tracking-[0.18em] text-green-700">Contact Giggs</span>
        <h3 className="mb-0 mt-2 text-[clamp(1.5rem,4vw,1.875rem)] font-black leading-tight tracking-[-0.02em] text-slate-950">How can we help?</h3>
        <p className="mb-0 mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Ask about booking a service, becoming a Gigger, an existing Gig, or anything else.
        </p>
      </div>

      <div className="grid min-w-0 gap-5 sm:grid-cols-2">
        <label className="block min-w-0">
          <span className="mb-2 block text-xs font-extrabold text-slate-700">Name *</span>
          <input className={inputClass} name="name" type="text" value={form.name} onChange={updateField} placeholder="Your name" autoComplete="name" />
        </label>
        <label className="block min-w-0">
          <span className="mb-2 block text-xs font-extrabold text-slate-700">Email *</span>
          <input className={inputClass} name="email" type="email" value={form.email} onChange={updateField} placeholder="you@example.com" autoComplete="email" />
        </label>
        <label className="block min-w-0">
          <span className="mb-2 block text-xs font-extrabold text-slate-700">Phone</span>
          <input className={inputClass} name="phone" type="tel" value={form.phone} onChange={updateField} placeholder="04xx xxx xxx" autoComplete="tel" />
        </label>
        <label className="block min-w-0">
          <span className="mb-2 block text-xs font-extrabold text-slate-700">I am a</span>
          <select className={`${inputClass} cursor-pointer`} name="interestedAs" value={form.interestedAs} onChange={updateField}>
            <option value="client">Client / looking for help</option>
            <option value="provider">Gigger / service provider</option>
          </select>
        </label>
        <label className="block min-w-0 sm:col-span-2">
          <span className="mb-2 block text-xs font-extrabold text-slate-700">Topic</span>
          <select className={`${inputClass} cursor-pointer`} name="topic" value={form.topic} onChange={updateField}>
            <option>General question</option>
            <option>Booking a service</option>
            <option>Becoming a Gigger</option>
            <option>Existing Gig</option>
            <option>Account support</option>
            <option>Business enquiry</option>
          </select>
        </label>
        <label className="block min-w-0 sm:col-span-2">
          <span className="mb-2 block text-xs font-extrabold text-slate-700">Suburb / postcode</span>
          <input className={inputClass} name="location" type="text" value={form.location} onChange={updateField} placeholder="Optional" autoComplete="postal-code" />
        </label>
        <label className="block min-w-0 sm:col-span-2">
          <span className="mb-2 block text-xs font-extrabold text-slate-700">Message *</span>
          <textarea className={`${inputClass} min-h-32 resize-y`} name="message" value={form.message} onChange={updateField} placeholder="Tell us what you need help with..." />
        </label>
      </div>

      {error && (
        <div className="mt-4 break-words rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-sm">{error}</div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className={`mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl border-0 bg-green-700 px-5 py-3.5 text-sm font-extrabold text-white shadow-[0_8px_20px_rgba(21,128,61,0.18)] hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60 ${buttonClass}`}
      >
        <Send size={17} />
        {status === 'submitting' ? 'Sending...' : 'Send message'}
      </button>
    </form>
  );
};

export default ContactForm;
